import { CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { type ReactNode, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import useColor from '@/hooks/domain/useColor';
import { cn } from '@/lib/cn';
import { radius } from '@/styles/sizes';

import { Flex, VFlex } from '../base';
import IconButton from '../Button/IconButton';
import { ParagraphStyle } from '../Typography/Typo';

type Props = {
  flow: 'vertical' | 'horizontal';
  css?: StyledCss;
  icon?: ReactNode | null;
  customIcon?: ReactNode | null;
  title?: ReactNode;
  desc?: ReactNode;
  buttons?: ReactNode[];
  variant?: 'error' | 'warning' | 'info';
  close?: () => void;
};

const Banner = (props: Props) => {
  const { variant = 'info' } = props;
  const color = useColor();

  const themeColor = useMemo(() => {
    switch (variant) {
      case 'error':
        return {
          textColor: 'text-red-800',
          bgColor: 'bg-red-800/4',
        };
      case 'warning':
        return {
          textColor: 'text-orange-300',
          bgColor: 'bg-orange-300/4',
        };
      case 'info':
        return {
          textColor: 'text-gray-700',
          bgColor: 'bg-gray-700/5',
        };
    }
  }, [variant]);

  const Icon = useCallback(() => {
    if (props.customIcon) return props.customIcon;
    const icon =
      typeof props.icon === 'undefined' ? <InfoCircleOutlined /> : props.icon;
    if (icon === null) return null;
    return icon;
  }, [props.customIcon, props.icon]);

  const Title = useCallback(() => {
    if (typeof props.title === 'undefined') return null;
    return <TitleSlot>{props.title}</TitleSlot>;
  }, [props.title]);

  const Desc = useCallback(() => {
    if (typeof props.desc === 'undefined') return null;
    return <DescSlot>{props.desc}</DescSlot>;
  }, [props.desc]);

  const Buttons = useCallback(() => {
    if (typeof props.buttons === 'undefined') return null;
    return <ButtonSlot>{props.buttons.map((b) => b)}</ButtonSlot>;
  }, [props.buttons]);

  const Close = useCallback(() => {
    if (typeof props.close === 'undefined') return null;
    return (
      <IconButton
        icon={<CloseOutlined style={{ color: color.gray600 }} />}
        onClick={props.close}
        outlined={false}
      />
    );
  }, [color.gray600, props.close]);

  if (props.flow === 'vertical') {
    return (
      <Style
        css={props.css}
        d="column"
        g={8}
        className={cn(themeColor.bgColor, themeColor.textColor)}
      >
        <Flex f={1} a="flex-start" g={8}>
          <Icon />
          <VFlex f={1} g={6}>
            <Title />
            <Desc />
          </VFlex>
          <Close />
        </Flex>
        <Buttons />
      </Style>
    );
  }

  return (
    <Style
      css={props.css}
      a="center"
      g={16}
      className={cn(themeColor.bgColor, themeColor.textColor)}
    >
      <Flex f={1} a="flex-start" g={8}>
        <Icon />
        <VFlex f={1} g={6}>
          <Title />
          <Desc />
        </VFlex>
      </Flex>
      <Flex g={8}>
        <Buttons />
        <Close />
      </Flex>
    </Style>
  );
};

const TitleSlot = styled(Flex)`
  ${ParagraphStyle({
    size: 3,
    weight: 'b',
  })}
`;

const DescSlot = styled(Flex)`
  ${ParagraphStyle({
    size: 3,
  })}
`;

const ButtonSlot = styled(Flex)`
  gap: 6px;
  align-items: center;
  justify-content: flex-end;
`;

const Style = styled(Flex)`
  padding: 12px 16px;
  border-radius: ${radius.banner}px;
`;

export default Banner;
